import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/auth/useAuth';
import { createOrganisation } from '@/features/organisations/data';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { useProjectFilter } from '@/features/projects';
import { createProject as createProjectRequest } from '@/features/projects/data';
import { useUpdateUserContext } from '@/features/users';
import {
  useAcceptInvite,
  useDeclineInvite,
  useInviteNotifications,
} from '@/features/users/hooks/users';
import { useNotifications } from '@/providers/notificationHooks';
import { s } from '@/theme/tokens';
import { uiToneSeverityMap } from '@/theme/uiTokens';
import { Box, Divider, Panel, Stack, Text } from '@/ui';
import { Alert } from '@/ui/primitives';
import { Button } from '@/ui/primitives/Button';
import { PageShell } from '@/ui/primitives/PageShell';
import { TextField } from '@/ui/primitives/TextField';
import type { AuthSession } from '@/auth/types';
import type { OrganisationDto } from '@flowqa/shared';
import type { ReactElement } from 'react';

type OnboardingPageProps = {
  session: AuthSession | null;
  organisations: Array<Pick<OrganisationDto, 'id' | 'name' | 'slug'>>;
};

export const OnboardingPage = ({
  session: _session,
  organisations: _organisations,
}: OnboardingPageProps): ReactElement => {
  const navigate = useNavigate();
  const { refreshSession, setSessionDirect, session: authSession } = useAuth();
  const session = authSession ?? _session;
  const { notify } = useNotifications();
  const { setSelectedOrganisationId } = useOrganisationFilter();
  const { setSelectedProjectId } = useProjectFilter();
  const updateUserContext = useUpdateUserContext();
  const inviteNotifications = useInviteNotifications();
  const acceptInvite = useAcceptInvite();
  const declineInvite = useDeclineInvite();

  const [organisationName, setOrganisationName] = useState('');
  const [projectName, setProjectName] = useState('');
  const [createdOrganisationId, setCreatedOrganisationId] = useState<string | null>(null);
  const [step, setStep] = useState<'organisation' | 'project'>('organisation');
  const [isCompleting, setIsCompleting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const isOrganisationNameValid = organisationName.trim().length > 0;
  const isProjectNameValid = projectName.trim().length > 0;

  useEffect(() => {
    if (step !== 'organisation') return;
    if (_organisations.length === 0) return;
    const existingOrganisationId = _organisations[0]?.id ?? null;
    if (!existingOrganisationId) return;
    setCreatedOrganisationId(existingOrganisationId);
    setSelectedOrganisationId(existingOrganisationId);
    setStep('project');
  }, [_organisations, setSelectedOrganisationId, step]);

  const completeOnboarding = async (organisationId: string, projectId: string): Promise<void> => {
    if (!session) return;
    const completionTimestamp = new Date().toISOString();
    try {
      setSelectedOrganisationId(organisationId);
      setSelectedProjectId(projectId);
      const updatedContext = await updateUserContext.mutateAsync({
        lastOrganisationId: organisationId,
        lastProjectId: projectId,
        onboardingCompletedAt: completionTimestamp,
      });

      await refreshSession();

      setSessionDirect({
        ...session,
        lastOrganisationId: updatedContext.lastOrganisationId ?? organisationId,
        lastProjectId: updatedContext.lastProjectId ?? projectId,
        onboardingCompleted: true,
      });
      void navigate('/execution-runs', { replace: true });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to finish onboarding';
      setErrorMessage(message);
      notify({ message, tone: 'red' });
    }
  };

  const handleCreateOrganisation = async (): Promise<void> => {
    if (!session) return;
    if (isCompleting) return;
    const trimmedOrganisationName = organisationName.trim();
    if (!trimmedOrganisationName) {
      setErrorMessage('Organisation name is required.');
      return;
    }

    setIsCompleting(true);
    setErrorMessage(null);
    try {
      const createdOrganisation = await createOrganisation({ name: trimmedOrganisationName });
      if (typeof window !== 'undefined') {
        window.dispatchEvent(
          new CustomEvent('flowqa-org-created', { detail: createdOrganisation }),
        );
      }
      notify({ message: 'Organisation created', tone: 'green' });
      setCreatedOrganisationId(createdOrganisation.id);
      setSelectedOrganisationId(createdOrganisation.id);
      setStep('project');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create organisation';
      setErrorMessage(message);
      notify({ message, tone: 'red' });
    } finally {
      setIsCompleting(false);
    }
  };

  const handleCreateProject = async (): Promise<void> => {
    if (!session) return;
    if (!createdOrganisationId) {
      setErrorMessage('Organisation is not ready yet.');
      return;
    }
    if (isCompleting) return;
    const trimmedProjectName = projectName.trim();
    if (!trimmedProjectName) {
      setErrorMessage('Project name is required.');
      return;
    }

    setIsCompleting(true);
    setErrorMessage(null);
    try {
      const createdProject = await createProjectRequest(
        { name: trimmedProjectName },
        session.tokens.accessToken,
        createdOrganisationId,
      );
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('flowqa-project-created', { detail: createdProject }));
      }
      notify({ message: 'Project created', tone: 'green' });
      await completeOnboarding(createdOrganisationId, createdProject.id);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create project';
      setErrorMessage(message);
      notify({ message, tone: 'red' });
    } finally {
      setIsCompleting(false);
    }
  };

  const handleAcceptInvite = async (inviteId: string): Promise<void> => {
    if (isCompleting) return;
    setIsCompleting(true);
    setErrorMessage(null);
    try {
      const invite = await acceptInvite.mutateAsync({ id: inviteId });
      notify({ message: 'Organisation joined', tone: 'green' });
      setCreatedOrganisationId(invite.organisationId);
      setSelectedOrganisationId(invite.organisationId);
      setStep('project');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to accept invite';
      setErrorMessage(message);
      notify({ message, tone: 'red' });
    } finally {
      setIsCompleting(false);
    }
  };

  const handleDeclineInvite = async (inviteId: string): Promise<void> => {
    try {
      await declineInvite.mutateAsync({ id: inviteId });
      notify({ message: 'Invite declined', tone: 'blue' });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to decline invite';
      setErrorMessage(message);
      notify({ message, tone: 'red' });
    }
  };

  const pendingInvites = inviteNotifications.data ?? [];
  const hasPendingInvites = pendingInvites.length > 0;

  return (
    <PageShell maxWidth="xs" paddingX="lg" paddingY="md" paddingBottom="md">
      <Box
        sx={{
          minHeight: { xs: 'calc(100dvh - 180px)', sm: 'calc(100dvh - 210px)' },
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
          pt: { xs: '14vh', sm: '12vh' },
        }}
      >
        <Stack gap="xl" alignItems="center" sx={{ width: '100%' }}>
          <Box sx={{ width: '100%' }}>
            <Panel gap="lg" contentGap="lg">
              <Stack gap="sm" alignItems="center">
                <Box
                  component="img"
                  src="/logo.png"
                  alt="FlowQA"
                  sx={{ height: '44px', width: '44px', display: 'block' }}
                />
                <Text role="sectionTitle" sx={{ textAlign: 'center' }}>
                  {step === 'organisation' ? 'Create Organisation' : 'Create Project'}
                </Text>
              </Stack>

              <Stack gap="lg">
                {step === 'organisation' ? (
                  <Stack
                    component="form"
                    gap="md"
                    onSubmit={(event) => {
                      event.preventDefault();
                      void handleCreateOrganisation();
                    }}
                  >
                    <Stack gap="sm">
                      <Text role="body" color="textSecondary">
                        Set your organisation name. This can be changed later.
                      </Text>
                      <Text role="body" color="textSecondary">
                        An organisation managers users and projects.
                      </Text>
                    </Stack>

                    <TextField
                      label="Organisation Name"
                      placeholder="My Organisation"
                      value={organisationName}
                      onChange={(event) => {
                        setOrganisationName(event.target.value);
                      }}
                      disabled={isCompleting}
                    />
                    {hasPendingInvites ? (
                      <>
                        <Divider />
                        <Stack gap="sm">
                          <Text role="label">Pending Invites</Text>
                          {pendingInvites.map((invite) => (
                            <Stack
                              key={invite.id}
                              direction={{ xs: 'column', sm: 'row' }}
                              justifyContent="space-between"
                              alignItems={{ xs: 'flex-start', sm: 'center' }}
                              gap="sm"
                            >
                              <Text role="body">{invite.organisationName}</Text>
                              <Stack direction="row" gap="sm">
                                <Button
                                  variant="secondary"
                                  onClick={() => {
                                    void handleDeclineInvite(invite.id);
                                  }}
                                  disabled={isCompleting}
                                >
                                  Decline
                                </Button>
                                <Button
                                  onClick={() => {
                                    void handleAcceptInvite(invite.id);
                                  }}
                                  loading={isCompleting}
                                >
                                  Join
                                </Button>
                              </Stack>
                            </Stack>
                          ))}
                        </Stack>
                      </>
                    ) : null}
                    <Button
                      type="submit"
                      onClick={handleCreateOrganisation}
                      loading={isCompleting}
                      disabled={!isOrganisationNameValid}
                      sx={{ mt: s('sm'), mb: s('md') }}
                    >
                      Create Organisation
                    </Button>
                  </Stack>
                ) : (
                  <Stack
                    component="form"
                    gap="md"
                    onSubmit={(event) => {
                      event.preventDefault();
                      void handleCreateProject();
                    }}
                  >
                    <Stack gap="sm">
                      <Text role="body" color="textSecondary">
                        Set your project name. This can be changed later.
                      </Text>
                      <Text role="body" color="textSecondary">
                        Results data will be stored against your project.
                      </Text>
                    </Stack>
                    <TextField
                      label="Project Name"
                      placeholder="My First Project"
                      value={projectName}
                      onChange={(event) => {
                        setProjectName(event.target.value);
                      }}
                      disabled={isCompleting}
                    />
                    <Button
                      type="submit"
                      onClick={handleCreateProject}
                      loading={isCompleting}
                      disabled={!isProjectNameValid}
                      sx={{ mt: s('sm'), mb: s('md') }}
                    >
                      Create Project
                    </Button>
                  </Stack>
                )}

                {errorMessage ? (
                  <Alert
                    severity={uiToneSeverityMap.red}
                    color="error"
                    variant="outlined"
                    sx={{ width: '100%' }}
                  >
                    {errorMessage}
                  </Alert>
                ) : null}
              </Stack>
            </Panel>
          </Box>
        </Stack>
      </Box>
    </PageShell>
  );
};
